import type { LeadTableRow } from "../api/leadsTableApi";
import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import { geoLocationPassesFilter } from "../utils/geoDirectorySelectOptions";

export type LeadSheetInterestFilter = "all" | "pending" | "interested" | "not_interested";

export type LeadSheetInterestKind = "interested" | "not_interested" | "other";

/** Classify `Leads.Status` (import / registration text) for filters and pill colours. */
export function leadSheetInterestKind(status: string | null | undefined): LeadSheetInterestKind {
  const s = String(status ?? "");
  if (/\bnot\s*interested\b/i.test(s)) return "not_interested";
  if (/\binterested\b/i.test(s)) return "interested";
  return "other";
}

/** Short label for Interested / Not interested; otherwise full sheet text. */
export function leadSheetInterestDisplayLabel(status: string | null | undefined): string {
  const k = leadSheetInterestKind(status);
  if (k === "interested") return "Interested";
  if (k === "not_interested") return "Not interested";
  return leadSheetStatusDisplayLabel(status);
}

export function leadSheetInterestMatchesFilter(
  row: Pick<LeadTableRow, "status">,
  filter: LeadSheetInterestFilter,
): boolean {
  if (filter === "all") return true;
  const kind = leadSheetInterestKind(row.status);
  if (filter === "interested") return kind === "interested";
  if (filter === "not_interested") return kind === "not_interested";
  return kind === "other";
}

export type LeadHoStatusFilter = "all" | "pending" | "approved" | "reject" | "on_hold";

export type LeadHoStatusKind = "approved" | "reject" | "on_hold" | "none";

/** Map `Leads.ho_status` into UI / filter buckets. */
export function leadHoStatusKind(hoStatus: string | null | undefined): LeadHoStatusKind {
  const s = String(hoStatus ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (!s) return "none";
  if (s === "approve" || s === "approved") return "approved";
  if (s === "reject" || s === "rejected") return "reject";
  if (s === "on_hold" || s === "onhold" || s === "hold") return "on_hold";
  return "none";
}

export function leadHoStatusShortLabel(hoStatus: string | null | undefined): string {
  const k = leadHoStatusKind(hoStatus);
  if (k === "approved") return "Approved";
  if (k === "reject") return "Reject";
  if (k === "on_hold") return "On hold";
  return "—";
}

/** Sheet `Leads.Status` from registration import (not updated by HO triage buttons). */
export function leadSheetStatusDisplayLabel(status: string | null | undefined): string {
  const s = String(status ?? "").trim();
  if (!s) return "—";
  if (s.toLowerCase() === "pending") return "Pending";
  return s;
}

/**
 * HO status filter match.
 * **pending** = list `Status` is **Interested** and `ho_status` is null/empty (awaiting HO decision).
 */
export function leadHoStatusMatchesFilter(
  row: Pick<LeadTableRow, "hoStatus" | "status">,
  filter: LeadHoStatusFilter,
): boolean {
  if (filter === "all") return true;
  const hoKind = leadHoStatusKind(row.hoStatus);
  if (filter === "pending") {
    return hoKind === "none" && leadSheetInterestKind(row.status) === "interested";
  }
  return hoKind === filter;
}

export function leadRowMatchesGeoFilter(
  row: LeadTableRow,
  opts: { district: string; mandal: string; village: string },
  geo?: GeoDirectoryStore | null,
): boolean {
  return geoLocationPassesFilter(geo, row, opts);
}
